// The box a section's product tiles sit in — the wrap grid, or the horizontal side-scroller when
// the section's `scrollProducts` is on.
//
// Every section preview flavor (featured / bundles / category / explicit products / saved search)
// used to hardcode `<div className='storefront-product-card-container'>`, so a section configured
// to side-scroll still previewed as a wrapping grid: the toggle existed in the section editor
// (StorefrontSectionEditor) and on the model, and the live store honored it, but the preview did
// not. Routing all five through this component is what makes the toggle mean the same thing on
// both sides.
//
// Mirrors ac-storefront-web's ProductScroller: a nowrap row that really does scroll, with chevrons
// that appear only when there is more to see and page the row by about one card. The chevrons are
// live rather than decorative — a merchant checking that a scrolling row holds what they expect
// has to be able to scroll it.

import React from 'react';
import { IconButton } from '@fluentui/react';
import { useCardChrome } from '../StorefrontCardChromeContext';

// Ported from the runtime's Components/Helpers/useSideScroll. Kept local (rather than generalized)
// because the preview has exactly one scrolling row; if a second appears, lift it as the runtime did.
const useSideScroll = (itemSelector: string, gap: number, deps: React.DependencyList = []) => {
    const ref = React.useRef<HTMLDivElement>(null);
    const [canScrollLeft, setCanScrollLeft] = React.useState(false);
    const [canScrollRight, setCanScrollRight] = React.useState(false);

    const checkScroll = React.useCallback(() => {
        const el = ref.current;
        if (!el) return;
        setCanScrollLeft(el.scrollLeft > 0);
        // +1 guards against sub-pixel rounding leaving the right arrow permanently visible.
        setCanScrollRight(el.scrollLeft + el.clientWidth + 1 < el.scrollWidth);
    }, []);

    // Re-measure on mount, whenever the row's contents change, and on any resize of it — the editor
    // canvas resizes as panels open and close, which changes whether the row overflows at all.
    React.useEffect(() => {
        checkScroll();
        const el = ref.current;
        if (!el) return;
        const ro = new ResizeObserver(() => checkScroll());
        ro.observe(el);
        return () => ro.disconnect();
        // Spread deps: this repo does not enable react-hooks/exhaustive-deps, so there is no
        // suppression comment here — naming the rule in one would break the build, since a
        // disable directive for an unconfigured rule is itself an eslint error.
    }, [checkScroll, ...deps]);

    const scrollByItem = React.useCallback((dir: 'left' | 'right') => {
        const el = ref.current;
        if (!el) return;
        // Page by ~one visible card (first card's width + the row gap), falling back to a full page.
        const first = el.querySelector(itemSelector) as HTMLElement | null;
        const step = first ? first.offsetWidth + gap : el.clientWidth;
        el.scrollBy({ left: dir === 'left' ? -step : step, behavior: 'smooth' });
    }, [itemSelector, gap]);

    return { ref, canScrollLeft, canScrollRight, checkScroll, scrollByItem };
};

// Matches the runtime scroller's flex gap, which is what a page step is measured against.
const SCROLLER_GAP = 16;

interface ISectionCardsContainerProps {
    /** The section's scrollProducts flag. Undefined/false = today's wrapping grid. */
    scroll?: boolean;
    isDark?: boolean;
    /** How many tiles are inside — re-measures the arrows when the section's contents change. */
    itemCount?: number;
    children?: React.ReactNode;
}

export const SectionCardsContainer: React.FC<ISectionCardsContainerProps> = (props) => {
    const chrome = useCardChrome();
    const { ref, canScrollLeft, canScrollRight, checkScroll, scrollByItem } =
        useSideScroll('.storefront-product-card', SCROLLER_GAP, [props.itemCount]);

    if (!props.scroll) {
        return <div className='storefront-product-card-container'>{props.children}</div>;
    }

    // Same translucent chevron the storefront uses, so the arrow stays readable over whichever
    // background the section band paints behind it.
    const arrowBg = props.isDark ? 'rgba(40,42,54,0.85)' : 'rgba(255,255,255,0.9)';

    return (
        <div className='storefront-card-scroller-wrap'>
            {canScrollLeft && (
                <IconButton
                    className='storefront-card-scroller-arrow storefront-card-scroller-arrow-left'
                    iconProps={{ iconName: 'ChevronLeft' }}
                    ariaLabel='Scroll left'
                    style={{ background: arrowBg, ...chrome.scrollButtonStyle }}
                    // The row sits inside the click-to-edit surfaces; paging it must not also open a
                    // style popover.
                    onClick={(e) => { e.stopPropagation(); scrollByItem('left'); }}
                />
            )}
            <div className='storefront-card-scroller' ref={ref} onScroll={checkScroll} style={chrome.scrollbarStyle}>
                {props.children}
            </div>
            {canScrollRight && (
                <IconButton
                    className='storefront-card-scroller-arrow storefront-card-scroller-arrow-right'
                    iconProps={{ iconName: 'ChevronRight' }}
                    ariaLabel='Scroll right'
                    style={{ background: arrowBg, ...chrome.scrollButtonStyle }}
                    onClick={(e) => { e.stopPropagation(); scrollByItem('right'); }}
                />
            )}
        </div>
    );
};

export default SectionCardsContainer;
